import React, { ChangeEvent, FC } from 'react'

import { UseFormRegister, UseFormWatch } from 'react-hook-form'
import { UseFormSetValue } from 'react-hook-form/dist/types/form'

import { Radio } from '@style-port/roov-ui-components-react'
import { Txt, Icon, Tooltip, TextField } from 'src/components/atoms'

import { useToast } from 'src/hooks'
import { SamlIdpFormEntity } from 'src/types'

type Props = {
  register: UseFormRegister<SamlIdpFormEntity>
  watch: UseFormWatch<SamlIdpFormEntity>
  setValue: UseFormSetValue<SamlIdpFormEntity>
  className?: string
}

export const MetadataSourceInput: FC<Props> = ({ register, setValue, watch, className }) => {
  const { displayErrorTextToast } = useToast()

  const handleFiles = (event: ChangeEvent<HTMLInputElement>) => {
    const { files } = event.currentTarget

    // 1度アップロードした後に再度アップロード試みてキャンセルを行った場合はlengthが0になる
    if (!files || files.length === 0) {
      return
    }

    if (files.length !== 1) {
      displayErrorTextToast('単体のファイルのみアップロードが可能です')
      return
    }

    const file = files[0]
    setValue('metadata.metadataFileName', file.name)

    // xmlのファイルの内容を metadataFile に設定する
    const reader = new FileReader()
    reader.readAsText(file)
    reader.addEventListener('load', async progressEvent => {
      const result = progressEvent.target?.result
      if (typeof result === 'string') {
        setValue('metadata.metadataFile', result)
      }
    })
  }

  const onRemoveMetadataFile = () => {
    setValue('metadata.metadataFileName', undefined)
    setValue('metadata.metadataFile', undefined)
  }

  return (
    <div className={className}>
      <Tooltip
        placement="top"
        content={
          <Txt sizeType="s">
            メタデータドキュメントを設定します。このドキュメントはIdPに
            <br />
            よって発行されます。このドキュメントには、発行者の名前、有効
            <br />
            期限に関する情報、およびIdPからのレスポンスを検証するため
            <br />
            に使用できるキーが含まれている必要があります。
          </Txt>
        }
      >
        <Txt className="tw-font-bold">メタデータソース形式</Txt>
      </Tooltip>
      <div className="tw-flex tw-mt-s2">
        <label htmlFor="metadataFile" className="tw-flex tw-justify-start tw-items-center">
          <Radio
            id="metadataFile"
            value="metadataFile"
            checked={watch('metadataSourceType') === 'metadataFile'}
            onClick={() => setValue('metadataSourceType', 'metadataFile')}
          />
          <Txt className="tw-ml-s-2 tw-w-[8rem]">ドキュメント形式</Txt>
        </label>
        {watch('metadataSourceType') === 'metadataFile' && (
          <>
            <label htmlFor="xmlUpload" className="tw-flex tw-items-center tw-ml-s2">
              <Icon
                path="upload/soft/line"
                size="--r1"
                className="tw-text-standardText-secondary"
              />
              <Txt className="tw-ml-s-3 tw-underline hover:tw-no-underline tw-cursor-pointer">
                {watch('metadata.metadataFileName') || 'XMLデータをアップロード'}
              </Txt>
              <input id="xmlUpload" type="file" hidden accept=".xml" onChange={handleFiles} />
            </label>
            {watch('metadata.metadataFileName') && (
              <Icon
                path="trash/soft/line"
                size="--r1"
                className="tw-ml-s-2 tw-text-standardText-secondary tw-cursor-pointer"
                onClick={onRemoveMetadataFile}
              />
            )}
          </>
        )}
      </div>
      <div className="tw-flex tw-mt-s2">
        <label htmlFor="metadataURL" className="tw-flex tw-justify-start tw-items-center">
          <Radio
            id="metadataURL"
            value="metadataURL"
            checked={watch('metadataSourceType') === 'metadataURL'}
            onClick={() => setValue('metadataSourceType', 'metadataURL')}
          />
          <Txt className="tw-ml-s-2 tw-w-[8rem]">URL形式</Txt>
        </label>
        {watch('metadataSourceType') === 'metadataURL' && (
          <TextField
            placeholder="URLを入力"
            className="tw-w-full"
            {...register('metadata.metadataURL')}
          />
        )}
      </div>
    </div>
  )
}
